// Icon pack registry.
//
// An "icon pack" is a folder of SVG files under src/assets/icon-packs/<pack>/,
// where each file name is a semantic icon name (e.g. delete.svg, inbox.svg).
// Adding a new pack is just dropping in a folder — it is auto-discovered here.
//
// SVGs are loaded as raw markup (see vue.config.js) and injected by
// SVGRepository.vue. Icons should use `fill="currentColor"` / `stroke="currentColor"`
// so colour is driven by the consumer; icons with an intentional fixed colour
// (e.g. the gold "important" star) keep their hardcoded fill.

// require.context returns the raw source string for each pack SVG.
//
// Vitest (Vite/ESM) has no require.context, so the call is wrapped: there it throws
// (ReferenceError) and the packs stay empty, which lets components that render
// SVGRepository mount in specs without every spec mocking it. Icon-pack specs pin
// the SVG assets directly instead.
//
// It MUST be a bare, unconditional `require.context(...)` call for webpack to see
// it. The previous guard — `typeof require.context === 'function'` — compiled to a
// runtime check on webpack's `require` shim, which has no `.context`, so the
// production build took the EMPTY branch and every pack icon in the app vanished
// (dev, 2026-09-22 — Pouria: "we missed too many icons on dev"). Never guard the
// call on a typeof again; a try/catch is the only shape webpack keeps intact.
const EMPTY_CTX = Object.assign(() => '', { keys: () => [] })
let ctx = EMPTY_CTX
try {
  // eslint-disable-next-line no-undef -- webpack's compile-time `require.context`
  ctx = require.context('@/assets/icon-packs', true, /\.svg$/)
} catch {
  ctx = EMPTY_CTX
}

// packs = { [packId]: { [iconName]: markup } }
const packs = {}
ctx.keys().forEach((key) => {
  const match = key.match(/^\.\/([^/]+)\/(.+)\.svg$/)
  if (!match) return
  const [, pack, name] = match
  const mod = ctx(key)
  const markup = mod && mod.default ? mod.default : mod
  ;(packs[pack] || (packs[pack] = {}))[name] = markup
})

export const DEFAULT_PACK = 'default'

// Friendly labels for known packs; anything else is title-cased from its id.
const PACK_LABELS = {
  default: 'Default',
  material: 'Material',
  vuesax: 'Vuesax (Outline)'
}

function labelFor(id) {
  return PACK_LABELS[id] || id.charAt(0).toUpperCase() + id.slice(1)
}

export function hasPack(id) {
  return Object.prototype.hasOwnProperty.call(packs, id)
}

// Available packs, default first then alphabetical, each with its icon count.
export function listPacks() {
  return Object.keys(packs)
    .sort((a, b) => {
      if (a === DEFAULT_PACK) return -1
      if (b === DEFAULT_PACK) return 1
      return a.localeCompare(b)
    })
    .map((id) => ({
      id,
      label: labelFor(id),
      count: Object.keys(packs[id]).length
    }))
}

// Resolve an icon's SVG markup for the given pack, falling back to the default
// pack when the active pack doesn't define that name. Returns '' if unknown.
export function getIcon(name, pack = DEFAULT_PACK) {
  if (!name) return ''
  const active = packs[pack]
  if (active && active[name]) return active[name]
  const fallback = packs[DEFAULT_PACK]
  return (fallback && fallback[name]) || ''
}

// A handful of representative names (present in the default pack) for previews
// in settings UIs — capped at 5.
export function previewNames() {
  const base = packs[DEFAULT_PACK] || {}
  return ['inbox', 'email', 'today', 'search', 'delete']
    .filter((n) => base[n])
    .slice(0, 5)
}
